import type {ReactNode} from 'react';

import type {AfriexAdminTransaction} from '@/types/AfriexAdminTransaction';
import type {SupportedAssets} from '@/types/AssetTypes';
import {AfriexTransactionType} from '@/types/Transaction';
import {formatValues, getTxTypeCheck} from '@/utils/dashboard';

const formatStatementValues = (
  transaction: AfriexAdminTransaction,
): {
  // Optional: TRANSFER transactions fall back to
  // `processorResponseMeta?.description`, which is genuinely absent when the
  // processor never sent one (see the "without processorResponseMeta" test).
  description: string | undefined;
  credit: ReactNode;
  debit: ReactNode;
  balance: ReactNode;
} => {
  const typeCheck = getTxTypeCheck(transaction);

  const description =
    transaction.type === AfriexTransactionType.TRANSFER
      ? transaction.processorResponseMeta?.description
      : transaction.type;
  // Withdrawals written before debitUserOnWithdrawal was flattened persisted
  // balances double-nested ({USD: {USD: 50}}), so those rows read as an object
  // and rendered NaN. Read through either shape.
  const readBalance = (asset: SupportedAssets | undefined) => {
    const entry = asset
      ? transaction?.meta?.balanceAfter?.[0]?.balances?.[asset]
      : undefined;
    return typeof entry === 'object' && entry !== null
      ? (entry as Record<string, number>)[asset as string]
      : entry;
  };

  const destinationBalanceAfter = readBalance(transaction?.destinationAsset);

  const sourceBalanceAfter = readBalance(transaction.sourceAsset);

  const asset = destinationBalanceAfter
    ? transaction?.destinationAsset
    : transaction?.sourceAsset;

  const amount = destinationBalanceAfter
    ? Number(transaction?.destinationAmount)
    : Number(transaction?.sourceAmount);

  const getAmountValue = (value: number) => formatValues(value, 'money');

  // A row with no recorded balance (a failed deposit never moved the wallet,
  // so its snapshot holds no amounts) shows a dash rather than NaN, as the
  // server-built PDF does.
  const showBalance = (
    value: number | undefined,
    balanceAsset: SupportedAssets | undefined,
  ): ReactNode =>
    value === undefined || Number.isNaN(Number(value)) ? (
      '-'
    ) : (
      <>
        {getAmountValue(Number(value))} {balanceAsset}
      </>
    );

  switch (typeCheck) {
    case AfriexTransactionType.DEPOSIT:
    case AfriexTransactionType.REFERRAL:
    case AfriexTransactionType.BONUS:
      return {
        description: description,
        credit: (
          <>
            {getAmountValue(amount)} {asset}
          </>
        ),
        debit: '',
        balance: showBalance(sourceBalanceAfter, asset),
      };
    case AfriexTransactionType.WITHDRAW:
    case AfriexTransactionType.TRANSFER:
      return {
        description: description,
        credit: '',
        debit: (
          <>
            {getAmountValue(amount)} {asset}
          </>
        ),
        balance: showBalance(
          destinationBalanceAfter || sourceBalanceAfter,
          asset,
        ),
      };
    case AfriexTransactionType.SWAP:
      return {
        description: description,
        credit: (
          <>
            {getAmountValue(Number(transaction.destinationAmount))}{' '}
            {transaction.destinationAsset}
          </>
        ),
        debit: (
          <>
            {getAmountValue(Number(transaction?.sourceAmount))}{' '}
            {transaction?.sourceAsset}
          </>
        ),
        balance: showBalance(sourceBalanceAfter, transaction.sourceAsset),
      };
    case AfriexTransactionType.REVERSAL:
      return {
        description: description,
        credit: (
          <>
            {getAmountValue(amount)} {asset}
          </>
        ),
        debit: '',
        balance: showBalance(
          destinationBalanceAfter || sourceBalanceAfter,
          asset,
        ),
      };
    default:
      return {
        description: description,
        credit: (
          <>
            {getAmountValue(amount)} {asset}
          </>
        ),
        debit: '',
        balance: showBalance(sourceBalanceAfter, asset),
      };
  }
};

export default formatStatementValues;
